import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";

// A4 GST tax invoice (HSN 998314, sequential numbering, CGST/SGST/IGST split).
// Renders inline so the user can hit Ctrl+P for a print-perfect PDF.
export default function PrintGstInvoice() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/gst/invoices/${id}`)
      .then((r) => setInv(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Unable to load invoice"));
  }, [id]);

  useEffect(() => {
    if (inv) setTimeout(() => window.print(), 600);
  }, [inv]);

  if (error) return <div className="p-10 text-center text-rose-600">{error}</div>;
  if (!inv) return <div className="p-10 text-center text-ink-muted">Loading invoice…</div>;

  const sup = inv.supplier || {};
  const rec = inv.recipient || {};
  const item = (inv.items || [])[0] || {};
  const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <>
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          body { background:#fff; }
          .no-print { display:none; }
        }
        .invoice-wrap {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          color:#111; max-width: 800px; margin: 24px auto; padding: 28px;
          background:#fff; border:1px solid #111;
        }
        .row { display:flex; }
        .col { flex:1; }
        .label { font-size:9px; text-transform:uppercase; letter-spacing:0.05em; color:#666; font-weight:600; }
        h1.title { font-size:14px; text-align:center; letter-spacing:0.1em; margin:0 0 16px; padding-bottom:8px; border-bottom:2px solid #111; text-transform:uppercase; font-weight:700; }
        .grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
        .box { border:1px solid #ccc; padding:10px; border-radius:4px; }
        table.items { width:100%; border-collapse:collapse; margin-top:14px; font-size:12px; }
        table.items th, table.items td { border:1px solid #ccc; padding:6px 8px; text-align:left; }
        table.items th { background:#f3f4f6; font-size:10px; text-transform:uppercase; letter-spacing:0.04em; }
        .right { text-align:right; }
        .totals { margin-top:8px; }
        .totals td { padding:4px 8px; font-size:12px; }
        .totals tr.grand td { border-top:2px solid #111; font-weight:700; font-size:14px; }
        .footer { margin-top:18px; font-size:11px; color:#444; }
      `}</style>
      <div className="no-print mb-2 flex justify-center gap-2 p-3">
        <button onClick={() => window.print()} className="rounded bg-brand px-4 py-2 text-sm text-white">Print / Save as PDF</button>
        <a href="/app/billing" className="rounded border px-4 py-2 text-sm">Back</a>
      </div>

      <div className="invoice-wrap" data-testid="gst-invoice-print">
        <h1 className="title">Tax Invoice</h1>

        <div className="row" style={{ borderBottom: "1px solid #ccc", paddingBottom: 10, marginBottom: 10 }}>
          <div className="col">
            <div style={{ fontWeight: 700, fontSize: 15 }}>{sup.legal_name}</div>
            {sup.trade_name && <div style={{ fontSize: 11, color: "#444" }}>({sup.trade_name})</div>}
            <div style={{ fontSize: 11, marginTop: 4 }}>{[sup.address_line1, sup.address_line2].filter(Boolean).join(", ")}</div>
            <div style={{ fontSize: 11 }}>{[sup.city, sup.state, sup.pincode].filter(Boolean).join(" - ")}</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>
              <span className="label">GSTIN</span> <span style={{ fontFamily: "monospace" }}>{sup.gstin}</span>
              {sup.pan && <> &nbsp;·&nbsp; <span className="label">PAN</span> <span style={{ fontFamily: "monospace" }}>{sup.pan}</span></>}
            </div>
            {(sup.email || sup.phone) && <div style={{ fontSize: 11 }}>{[sup.email, sup.phone].filter(Boolean).join(" · ")}</div>}
          </div>
          <div className="col" style={{ textAlign: "right" }}>
            <div className="label">Invoice no</div>
            <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "monospace" }} data-testid="gst-invoice-no">{inv.invoice_no}</div>
            <div className="label" style={{ marginTop: 6 }}>Invoice date</div>
            <div style={{ fontSize: 13 }}>{inv.invoice_date}</div>
            <div className="label" style={{ marginTop: 6 }}>Place of supply</div>
            <div style={{ fontSize: 12 }}>{inv.place_of_supply}</div>
            <div className="label" style={{ marginTop: 6 }}>Reverse charge</div>
            <div style={{ fontSize: 12 }}>{inv.reverse_charge}</div>
          </div>
        </div>

        <div className="grid-2">
          <div className="box">
            <div className="label">Bill to (Recipient)</div>
            <div style={{ fontWeight: 700, marginTop: 4 }}>{rec.legal_name}</div>
            <div style={{ fontSize: 11 }}>{[rec.address_line1, rec.address_line2].filter(Boolean).join(", ")}</div>
            <div style={{ fontSize: 11 }}>{[rec.city, rec.state, rec.pincode].filter(Boolean).join(" - ")}</div>
            {rec.gstin && <div style={{ fontSize: 11, marginTop: 4 }}><span className="label">GSTIN</span> <span style={{ fontFamily: "monospace" }}>{rec.gstin}</span></div>}
            {!rec.gstin && <div style={{ fontSize: 11, marginTop: 4, fontStyle: "italic", color: "#999" }}>Unregistered customer</div>}
            <div style={{ fontSize: 11 }}><span className="label">State code</span> {rec.state_code}</div>
          </div>
          <div className="box">
            <div className="label">Subscription details</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Plan: <b>{inv.plan_name}</b></div>
            {inv.period_start && <div style={{ fontSize: 11 }}>Period: {inv.period_start?.slice(0, 10)} → {inv.period_end?.slice(0, 10)}</div>}
            {inv.payment_transaction_id && <div style={{ fontSize: 10, fontFamily: "monospace", color: "#666", marginTop: 6 }}>Txn: {inv.payment_transaction_id}</div>}
          </div>
        </div>

        <table className="items">
          <thead>
            <tr>
              <th style={{ width: 30 }}>#</th>
              <th>Description of services</th>
              <th style={{ width: 70 }}>HSN/SAC</th>
              <th style={{ width: 40 }}>Qty</th>
              <th style={{ width: 80 }} className="right">Rate</th>
              <th style={{ width: 90 }} className="right">Taxable value</th>
              {inv.is_inter_state
                ? <th style={{ width: 110 }} className="right">IGST<br />({fmt(item.igst_rate)}%)</th>
                : <>
                    <th style={{ width: 80 }} className="right">CGST<br />({fmt(item.cgst_rate)}%)</th>
                    <th style={{ width: 80 }} className="right">SGST<br />({fmt(item.sgst_rate)}%)</th>
                  </>}
              <th style={{ width: 90 }} className="right">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>1</td>
              <td>{item.description}</td>
              <td style={{ fontFamily: "monospace" }}>{item.hsn_sac}</td>
              <td>{item.quantity}</td>
              <td className="right">{fmt(item.unit_price)}</td>
              <td className="right">{fmt(item.taxable_value)}</td>
              {inv.is_inter_state
                ? <td className="right">{fmt(item.igst_amt)}</td>
                : <>
                    <td className="right">{fmt(item.cgst_amt)}</td>
                    <td className="right">{fmt(item.sgst_amt)}</td>
                  </>}
              <td className="right">{fmt(inv.grand_total)}</td>
            </tr>
          </tbody>
        </table>

        <div style={{ display: "flex", marginTop: 12 }}>
          <div style={{ flex: 1, fontSize: 11 }}>
            <div className="label">Amount in words</div>
            <div style={{ fontWeight: 600 }}>{inv.grand_total_words}</div>
          </div>
          <table className="totals" style={{ width: 260 }}>
            <tbody>
              <tr><td>Taxable value</td><td className="right">₹ {fmt(inv.taxable_total)}</td></tr>
              {inv.is_inter_state
                ? <tr><td>IGST</td><td className="right">₹ {fmt(inv.igst_total)}</td></tr>
                : <>
                    <tr><td>CGST</td><td className="right">₹ {fmt(inv.cgst_total)}</td></tr>
                    <tr><td>SGST</td><td className="right">₹ {fmt(inv.sgst_total)}</td></tr>
                  </>}
              <tr className="grand"><td>Grand total</td><td className="right" data-testid="gst-grand-total">₹ {fmt(inv.grand_total)}</td></tr>
            </tbody>
          </table>
        </div>

        {(sup.bank_account_no || sup.bank_ifsc) && (
          <div className="box footer" style={{ marginTop: 16 }}>
            <div className="label">Bank details (for NEFT / RTGS)</div>
            <div>{sup.bank_name}{sup.bank_branch ? ` · ${sup.bank_branch}` : ""}</div>
            <div>A/c: <span style={{ fontFamily: "monospace" }}>{sup.bank_account_no}</span> · IFSC: <span style={{ fontFamily: "monospace" }}>{sup.bank_ifsc}</span></div>
          </div>
        )}

        <div className="footer" style={{ whiteSpace: "pre-line" }}>
          <div className="label">Terms & conditions</div>
          {sup.invoice_terms}
        </div>

        <div style={{ marginTop: 28, display: "flex", justifyContent: "space-between" }}>
          <div className="footer">For receiver's signature</div>
          <div className="footer right" style={{ textAlign: "right" }}>
            For <b>{sup.legal_name}</b>
            <div style={{ marginTop: 30 }}>{sup.signatory_name || "Authorised Signatory"}</div>
          </div>
        </div>
      </div>
    </>
  );
}
